import React, { useRef, useState } from 'react';
import { Message, JobRecommendation } from '../types';
import { formatTime } from '../utils/timeUtils';

/** Quick response chip options for David messages */
const QUICK_RESPONSES = [
  { text: "I like these jobs", emoji: "👍" },
  { text: "Not relevant to me", emoji: "🚫" },
  { text: "Show me more like this", emoji: "🔄" },
  { text: "Different role please", emoji: "🔀" },
];

interface MessageBubbleProps {
  /**
   * Message data
   */
  message: Message;

  /**
   * Is this message sent by the current user
   */
  isSent: boolean;

  /**
   * Sender name (for received messages)
   */
  senderName?: string;

  /**
   * Sender avatar (for received messages)
   */
  senderAvatar?: string;

  /**
   * Should this message be grouped with the previous one (hide avatar/name)
   */
  isGrouped?: boolean;

  /**
   * Callback when reply button is clicked
   */
  onReply?: (message: Message) => void;

  /**
   * Callback when retry button is clicked on a failed message (7d)
   */
  onRetry?: (message: Message) => void;

  /**
   * Callback when thumbs up/down is clicked (David messages)
   */
  onFeedback?: (messageId: string, type: 'thumbs_up' | 'thumbs_down') => void;

  /**
   * Callback when a quick response chip is clicked (David messages)
   */
  onQuickResponse?: (messageId: string, text: string) => void;

  /**
   * Is this a David bot message (enables feedback UI)
   */
  isDavidMessage?: boolean;

  /**
   * Callback when a job recommendation card is clicked (opens detail panel)
   */
  onJobClick?: (jobId: string) => void;
}

/**
 * MessageBubble Component
 * Improved: Better shadows, hover states, smoother animations
 */
const MessageBubble: React.FC<MessageBubbleProps> = ({
  message,
  isSent,
  senderName,
  senderAvatar,
  isGrouped = false,
  onReply,
  onRetry,
  onFeedback,
  onQuickResponse,
  isDavidMessage = false,
  onJobClick,
}) => {
  const [feedbackState, setFeedbackState] = useState<'thumbs_up' | 'thumbs_down' | null>(
    message.feedback?.type || null
  );
  const [selectedQuickResponse, setSelectedQuickResponse] = useState<string | null>(
    message.quick_response?.text || null
  );
  // 7e: Audio playback state
  const audioRef = useRef<HTMLAudioElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [audioCurrentTime, setAudioCurrentTime] = useState(0);

  /**
   * Format file size
   */
  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  /**
   * Format duration in seconds to m:ss
   */
  const formatDuration = (seconds: number): string => {
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  /**
   * Toggle audio play/pause (7e)
   */
  const toggleAudio = () => {
    if (!audioRef.current) return;
    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play().catch(() => {
        // Autoplay policy blocked — ignore
      });
    }
    setIsPlaying(!isPlaying);
  };

  /**
   * Render voice note message with functional playback (7e)
   */
  const renderVoiceNote = (attachment: { url: string; name: string; size: number; duration?: number }) => (
    <div className="flex flex-row items-center gap-3 p-3 bg-white rounded-lg" style={{ minWidth: '200px' }}>
      {/* Hidden audio element */}
      <audio
        ref={audioRef}
        src={attachment.url}
        onTimeUpdate={() => setAudioCurrentTime(audioRef.current?.currentTime || 0)}
        onEnded={() => { setIsPlaying(false); setAudioCurrentTime(0); }}
      />

      {/* Play/Pause button */}
      <button
        onClick={toggleAudio}
        className="w-10 h-10 flex items-center justify-center bg-[#015FA6] rounded-full hover:bg-[#014580] transition-colors flex-shrink-0"
        aria-label={isPlaying ? 'Pause' : 'Play'}
      >
        {isPlaying ? (
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="3" y="2" width="4" height="12" rx="1" fill="white"/>
            <rect x="9" y="2" width="4" height="12" rx="1" fill="white"/>
          </svg>
        ) : (
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M3 2L13 8L3 14V2Z" fill="white"/>
          </svg>
        )}
      </button>

      {/* Waveform visualization */}
      <div className="flex flex-row items-center gap-1 flex-1">
        {[3, 5, 4, 6, 5, 7, 6, 5, 4, 6, 5, 4, 3, 5, 4, 6, 5, 4, 3, 5].map((height, index) => (
          <div
            key={index}
            className="w-1 rounded-full"
            style={{ height: `${height * 4}px`, backgroundColor: '#015FA6' }}
          />
        ))}
      </div>

      {/* Time display: current position when playing, total duration when stopped */}
      <span className="font-manrope font-normal text-xs text-[#757575] flex-shrink-0">
        {isPlaying
          ? formatDuration(audioCurrentTime)
          : formatDuration(attachment.duration || 0)}
      </span>
    </div>
  );

  /**
   * Render file attachment
   */
  const renderFileAttachment = (attachment: { type: string; url: string; name: string; size: number }) => (
    <a
      href={attachment.url}
      target="_blank"
      rel="noopener noreferrer"
      className="flex flex-row items-center gap-3 p-3 bg-white rounded-lg hover:bg-[#F9F9F9] transition-colors border border-[#E5E7EB]"
    >
      {/* File icon */}
      <div className="w-10 h-10 flex items-center justify-center bg-[#F1F3F4] rounded-lg">
        {attachment.type.startsWith('image/') ? (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="3" y="3" width="18" height="18" rx="2" stroke="#015FA6" strokeWidth="2"/>
            <circle cx="8.5" cy="8.5" r="1.5" fill="#015FA6"/>
            <path d="M21 15L16 10L5 21" stroke="#015FA6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        ) : (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M13 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V9L13 2Z" stroke="#015FA6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M13 2V9H20" stroke="#015FA6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        )}
      </div>

      {/* File info */}
      <div className="flex flex-col flex-1">
        <p className="font-manrope font-medium text-sm text-[#1A1A1A] truncate">
          {attachment.name}
        </p>
        <p className="font-manrope font-normal text-xs text-[#757575]">
          {formatFileSize(attachment.size)}
        </p>
      </div>

      {/* Download icon */}
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M10 13L6 9H14L10 13Z" fill="#757575"/>
        <path d="M10 3V13" stroke="#757575" strokeWidth="2" strokeLinecap="round"/>
        <path d="M3 15V16C3 16.5304 3.21071 17.0391 3.58579 17.4142C3.96086 17.7893 4.46957 18 5 18H15C15.5304 18 16.0391 17.7893 16.4142 17.4142C16.7893 17.0391 17 16.5304 17 16V15" stroke="#757575" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    </a>
  );

  /**
   * Render image attachment
   */
  const renderImageAttachment = (attachment: { url: string; name: string }) => (
    <a
      href={attachment.url}
      target="_blank"
      rel="noopener noreferrer"
      className="block max-w-xs rounded-lg overflow-hidden border border-[#E5E7EB]"
    >
      <img
        src={attachment.url}
        alt={attachment.name}
        className="w-full h-auto object-cover"
      />
    </a>
  );

  /**
   * Render job recommendation cards (David messages)
   */
  const renderJobRecommendations = (jobs: JobRecommendation[]) => (
    <div className="flex flex-col gap-2 mt-2">
      {jobs.map((job, idx) => {
        const scorePercent = Math.round(job.match_score * 100);
        const scoreColor = scorePercent >= 70 ? 'bg-green-100 text-green-700' : scorePercent >= 40 ? 'bg-yellow-100 text-yellow-700' : 'bg-gray-100 text-gray-600';
        return (
          <button
            key={idx}
            onClick={() => onJobClick ? onJobClick(job.job_id) : window.open(`/candidate/jobs?highlight=${job.job_id}`, '_blank')}
            className="block w-full text-left p-3 bg-white rounded-xl border border-[#E5E7EB] hover:border-[#015FA6]/40 hover:shadow-sm transition-all cursor-pointer"
          >
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="font-manrope font-semibold text-sm text-[#0B111E] truncate">{job.title}</p>
                <p className="font-manrope text-xs text-[#6B7280] mt-0.5">{job.company_name}</p>
                <div className="flex items-center gap-2 mt-1.5">
                  {job.location && (
                    <span className="font-manrope text-[11px] text-[#9CA3AF]">{job.location}</span>
                  )}
                  {job.employment_type && (
                    <>
                      <span className="w-1 h-1 bg-[#D1D5DB] rounded-full" />
                      <span className="font-manrope text-[11px] text-[#9CA3AF]">{job.employment_type}</span>
                    </>
                  )}
                </div>
              </div>
              {job.match_score > 0 && (
                <span className={`flex-shrink-0 px-2 py-0.5 rounded-lg font-manrope font-semibold text-[11px] ${scoreColor}`}>
                  {scorePercent}%
                </span>
              )}
            </div>
          </button>
        );
      })}
    </div>
  );

  // 7g: Reply-to context from metadata
  const replyToContent: string | undefined = message.metadata?.reply_to_content;
  const replyToSender: string | undefined = message.metadata?.reply_to_sender;

  return (
    <div
      className={`flex flex-row gap-3 ${isGrouped ? 'mt-0.5' : 'mt-4'} ${isSent ? 'justify-end pl-16' : 'justify-start pr-16'} group/message`}
    >
      {/* Avatar for received messages - hide if grouped */}
      {!isSent && (
        <div className="w-9 h-9 flex-shrink-0">
          {!isGrouped && senderAvatar ? (
            <img
              src={senderAvatar}
              alt={senderName || 'Sender'}
              className="w-9 h-9 rounded-xl object-cover shadow-sm"
            />
          ) : !isGrouped ? (
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#015FA6] to-[#0176C9] flex items-center justify-center shadow-sm">
              <span className="font-manrope font-semibold text-sm text-white">
                {senderName?.charAt(0).toUpperCase() || '?'}
              </span>
            </div>
          ) : null}
        </div>
      )}

      {/* Message content */}
      <div className={`flex flex-col gap-1 max-w-[65%] ${isSent ? 'items-end' : 'items-start'}`}>
        {/* Sender name for received messages - hide if grouped */}
        {!isSent && senderName && !isGrouped && (
          <p className="font-manrope font-semibold text-xs text-[#6B7280] px-1 mb-0.5">
            {senderName}
          </p>
        )}

        {/* Message bubble */}
        <div
          className={`relative px-4 py-2.5 rounded-2xl transition-all duration-150 ${
            isSent
              ? `bg-[#015FA6] text-white ${isGrouped ? 'rounded-br-lg' : 'rounded-br-md'} shadow-[0_1px_2px_rgba(1,95,166,0.15)] hover:shadow-[0_2px_8px_rgba(1,95,166,0.2)]`
              : `bg-white text-[#1A1A1A] border border-[#E5E7EB] ${isGrouped ? 'rounded-bl-lg' : 'rounded-bl-md'} shadow-sm hover:shadow-md hover:border-[#D1D5DB]`
          }`}
        >
          {/* 7g: Reply-to quoted snippet */}
          {replyToContent && (
            <div
              className={`mb-2 pl-2.5 border-l-2 rounded-sm ${
                isSent ? 'border-white/50 bg-white/10' : 'border-[#015FA6] bg-[#F3F8FC]'
              }`}
            >
              {replyToSender && (
                <p className={`font-manrope font-semibold text-[10px] mb-0.5 ${isSent ? 'text-white/80' : 'text-[#015FA6]'}`}>
                  {replyToSender}
                </p>
              )}
              <p className={`font-manrope font-normal text-xs truncate ${isSent ? 'text-white/70' : 'text-[#6B7280]'}`}>
                {replyToContent.length > 80 ? replyToContent.slice(0, 80) + '…' : replyToContent}
              </p>
            </div>
          )}

          {/* Text content */}
          {(message.message_type === 'text' || message.message_type === 'job_recommendation' || !message.message_type) && (
            <p className="font-manrope font-normal text-sm whitespace-pre-wrap break-words leading-relaxed">
              {message.content}
            </p>
          )}

          {/* Job recommendation cards (David messages) */}
          {message.job_recommendations && message.job_recommendations.length > 0 && (
            renderJobRecommendations(message.job_recommendations)
          )}

          {/* Thumbs up/down feedback (David messages only) */}
          {isDavidMessage && !isSent && message.job_recommendations && message.job_recommendations.length > 0 && (
            <div className="flex items-center gap-1 mt-3 pt-2 border-t border-[#E5E7EB]">
              <span className="font-manrope text-[11px] text-[#9CA3AF] mr-1">Was this helpful?</span>
              <button
                onClick={() => {
                  const newType = feedbackState === 'thumbs_up' ? null : 'thumbs_up' as const;
                  setFeedbackState(newType);
                  if (newType && onFeedback) onFeedback(message.id, newType);
                }}
                className={`p-1.5 rounded-lg transition-all ${
                  feedbackState === 'thumbs_up'
                    ? 'bg-green-100 text-green-600'
                    : 'hover:bg-[#F3F4F6] text-[#9CA3AF] hover:text-[#6B7280]'
                }`}
                title="Helpful"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3H14z"/>
                  <path d="M7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3"/>
                </svg>
              </button>
              <button
                onClick={() => {
                  const newType = feedbackState === 'thumbs_down' ? null : 'thumbs_down' as const;
                  setFeedbackState(newType);
                  if (newType && onFeedback) onFeedback(message.id, newType);
                }}
                className={`p-1.5 rounded-lg transition-all ${
                  feedbackState === 'thumbs_down'
                    ? 'bg-red-100 text-red-500'
                    : 'hover:bg-[#F3F4F6] text-[#9CA3AF] hover:text-[#6B7280]'
                }`}
                title="Not helpful"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M10 15v4a3 3 0 0 0 3 3l4-9V2H5.72a2 2 0 0 0-2 1.7l-1.38 9a2 2 0 0 0 2 2.3H10z"/>
                  <path d="M17 2h2.67A2.31 2.31 0 0 1 22 4v7a2.31 2.31 0 0 1-2.33 2H17"/>
                </svg>
              </button>
            </div>
          )}

          {/* Voice note */}
          {message.message_type === 'voice' && message.attachments && message.attachments[0] && (
            renderVoiceNote(message.attachments[0])
          )}

          {/* File attachments */}
          {message.message_type === 'file' && message.attachments && message.attachments.length > 0 && (
            <div className="flex flex-col gap-2">
              {message.attachments.map((attachment, index) => (
                <div key={index}>
                  {attachment.type.startsWith('image/')
                    ? renderImageAttachment(attachment)
                    : renderFileAttachment(attachment)}
                </div>
              ))}
              {message.content && (
                <p className="font-manrope font-normal text-sm whitespace-pre-wrap break-words mt-2">
                  {message.content}
                </p>
              )}
            </div>
          )}
        </div>

        {/* Timestamp, read status, and failed indicator */}
        <div className={`flex items-center gap-1.5 px-1 mt-0.5 ${isSent ? 'justify-end' : 'justify-start'}`}>
          {/* 7d: Failed state indicator */}
          {message.status === 'failed' ? (
            <>
              <span className="font-manrope font-normal text-[11px] text-[#DC2626]">
                Failed to send
              </span>
              {onRetry && (
                <button
                  onClick={() => onRetry(message)}
                  className="flex items-center gap-1 text-[11px] font-medium text-[#015FA6] hover:underline"
                  title="Retry sending"
                >
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M1 4V10H7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M3.51 15C4.15839 16.8404 5.38734 18.4202 7.01166 19.5014C8.63598 20.5826 10.5677 21.1066 12.5157 20.9945C14.4637 20.8824 16.3226 20.1402 17.8121 18.8798C19.3017 17.6193 20.3413 15.909 20.7742 14.0064C21.2070 12.1037 21.0101 10.112 20.2126 8.33108C19.4152 6.55019 18.0605 5.07685 16.3528 4.13322C14.6451 3.18959 12.6769 2.82694 10.7447 3.09951C8.81245 3.37208 7.02091 4.26574 5.64 5.64L1 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Retry
                </button>
              )}
            </>
          ) : (
            <>
              <span className="font-manrope font-normal text-[11px] text-[#9CA3AF]">
                {formatTime(message.timestamp)}
              </span>

              {/* Read indicators for sent messages */}
              {isSent && (
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  {message.read_by?.candidate && message.read_by?.recruiter ? (
                    // Double check (read)
                    <>
                      <path d="M2 8L5 11L9 7" stroke="#015FA6" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M6 8L9 11L13 7" stroke="#015FA6" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </>
                  ) : (
                    // Single check (delivered)
                    <path d="M3 8L6 11L13 4" stroke="#9CA3AF" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  )}
                </svg>
              )}
            </>
          )}
        </div>

        {/* Quick response chips (David messages — below the bubble) */}
        {isDavidMessage && !isSent && message.job_recommendations && message.job_recommendations.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-2 px-1">
            {selectedQuickResponse ? (
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#015FA6] text-white font-manrope text-xs font-medium">
                {QUICK_RESPONSES.find(r => r.text === selectedQuickResponse)?.emoji} {selectedQuickResponse}
              </span>
            ) : (
              QUICK_RESPONSES.map((chip) => (
                <button
                  key={chip.text}
                  onClick={() => {
                    setSelectedQuickResponse(chip.text);
                    if (onQuickResponse) onQuickResponse(message.id, chip.text);
                  }}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[#E5E7EB] bg-white hover:border-[#015FA6]/40 hover:bg-[#F3F8FC] font-manrope text-xs text-[#374151] hover:text-[#015FA6] transition-all"
                >
                  <span>{chip.emoji}</span>
                  {chip.text}
                </button>
              ))
            )}
          </div>
        )}

        {/* Reply button (on hover) */}
        {onReply && message.status !== 'failed' && (
          <button
            onClick={() => onReply(message)}
            className="opacity-0 group-hover/message:opacity-100 flex items-center gap-1.5 px-2.5 py-1.5 mt-1 text-xs font-medium text-[#6B7280] hover:text-[#015FA6] hover:bg-[#F3F4F6] rounded-lg transition-all duration-150"
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M5 3L1 7L5 11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M1 7H9C10.6569 7 12 8.34315 12 10V11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Reply
          </button>
        )}
      </div>
    </div>
  );
};

export default MessageBubble;
